import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  ListGroup,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";

import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaTrash,
  FaTruck,
  FaCheckCircle,
  FaBoxOpen,
  FaArrowLeft,
  FaListAlt,
  FaMoneyBillWave,
} from "react-icons/fa";

import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Toast from "react-hot-toast";

import {
  useGetOrderByIdQuery,
  useMarkOrderDeliveredMutation,
  useDeleteOrderMutation,
} from "../slices/orderApiSlice";

import "../css/OrderScreen.css";

const OrderScreen = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const { userInfo } = useSelector((state) => state.auth);
  const role = userInfo?.role;

  const {
    data: order,
    refetch,
    isLoading,
    error,
  } = useGetOrderByIdQuery(orderId, { skip: !orderId });

  const [markDelivered, { isLoading: loadingDeliver }] =
    useMarkOrderDeliveredMutation();

  const [deleteOrder, { isLoading: loadingDelete }] =
    useDeleteOrderMutation();

  /* ---------- Permissions (same as App) ---------- */
  const canDeliver =
    (role === "vendor" || role === "admin" || role === "delivery") &&
    order?.status !== "Delivered";

  const canDelete = order?.status !== "Delivered";

  /* ---------- Handlers ---------- */
  const deliverHandler = async () => {
    try {
      await markDelivered(orderId).unwrap();
      refetch();
      Toast.success("Order marked as delivered.");
    } catch (err) {
      Toast.error(err?.data?.message || err.message);
    }
  };

  const deleteHandler = async () => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;

    try {
      await deleteOrder(orderId).unwrap();
      Toast.success("Order deleted successfully.");
      navigate("/my-orders");
    } catch (err) {
      Toast.error(err?.data?.message || err.message);
    }
  };

  /* ---------- Loading / Error ---------- */
  if (isLoading)
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );

  if (error)
    return (
      <Container className="py-4">
        <Alert variant="danger">
          ❌ {error?.data?.message || error?.message}
        </Alert>
      </Container>
    );

  if (!order)
    return (
      <Container className="py-4">
        <Alert variant="warning">❌ Order not found</Alert>
      </Container>
    );

  /* ---------- Calculations (same as App) ---------- */
  const productSubtotal = order.products?.reduce(
    (sum, item) =>
      sum +
      (item.totalItemAmount ??
        (item.unitPrice || 0) * (item.quantity || 0)),
    0
  );

  const deliveryCharge = order.deliveryCharge || 0;

  return (
    <Container className="py-4 order-screen">
      {/* 🔙 Go Home */}
      <Button
        variant="link"
        className="go-back-btn"
        onClick={() => navigate("/")}
      >
        <FaArrowLeft className="me-2" /> Go Home
      </Button>

      {/* 🔠 Order Header */}
      <Card className="mb-4 shadow-sm glass-card">
        <Card.Header className="gradient-header text-white">
          <FaListAlt className="me-2" />
          Order #{(order._id || "").slice(-6)}
        </Card.Header>

        <Card.Body>
          {/* 🚚 Shipping Information */}
          <Card className="mb-3 shadow-sm section-card">
            <Card.Header className="section-header bg-info text-white">
              <FaTruck className="me-2" /> Shipping Information
            </Card.Header>

            <Card.Body>
              <div className="order-info-row">
                <FaUser className="me-2" />
                {order.customer?.name || "N/A"}
              </div>

              <div className="order-info-row">
                <FaEnvelope className="me-2" />
                {order.customer?.email || "N/A"}
              </div>

              <div className="order-info-row">
                <FaPhone className="me-2" />
                {order.shippingAddress?.phoneNumber || "N/A"}
              </div>

              <div className="order-info-row">
                <FaMapMarkerAlt className="me-2" />
                {order.shippingAddress?.address},{" "}
                {order.shippingAddress?.area},{" "}
                {order.shippingAddress?.city} -{" "}
                {order.shippingAddress?.pincode}
              </div>

              <div className="order-status mt-2">
                {order.status === "Delivered" ? (
                  <span className="text-success fw-bold">
                    Delivered <FaCheckCircle />
                  </span>
                ) : (
                  <span className="text-danger fw-bold">
                    Not Delivered ❌
                  </span>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* 📦 Items */}
          <Card className="shadow-sm section-card">
            <Card.Header className="section-header bg-success text-white">
              <FaBoxOpen className="me-2" /> Items in This Order
            </Card.Header>

            <Card.Body>
              {order.products?.length > 0 ? (
                <ListGroup variant="flush">
                  {order.products.map((item, idx) => {
                    const unitPrice =
                      typeof item.unitPrice === "number"
                        ? item.unitPrice
                        : 0;

                    const subtotal =
                      typeof item.totalItemAmount === "number"
                        ? item.totalItemAmount
                        : unitPrice * item.quantity;

                    return (
                      <ListGroup.Item
                        key={idx}
                        className="d-flex align-items-center order-item"
                      >
                        <div className="order-item-img placeholder-box">
                          📦
                        </div>

                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-1">
                            {item.name || "Unnamed Product"}
                          </h6>

                          <div className="item-sub">
                            ₹{unitPrice} × {item.quantity}
                          </div>

                          <div className="item-price">
                            ₹{subtotal.toFixed(2)}
                          </div>
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              ) : (
                <Alert variant="warning">
                  No items in this order.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* 🧾 Summary & Actions */}
      <Row>
        <Col md={6}>
          <Card className="shadow-sm glass-card">
            <Card.Header className="gradient-header text-white">
              <FaMoneyBillWave className="me-2" />
              Order Summary
            </Card.Header>

            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item className="d-flex justify-content-between">
                  Products Total:
                  <strong>₹{productSubtotal.toFixed(2)}</strong>
                </ListGroup.Item>

                <ListGroup.Item className="d-flex justify-content-between">
                  Delivery Charge:
                  <strong>
                    ₹{deliveryCharge.toFixed(2)}
                  </strong>
                </ListGroup.Item>

                <ListGroup.Item className="d-flex justify-content-between">
                  Total Discount:
                  <strong>
                    ₹{order.totalDiscount?.toFixed(2) || "0.00"}
                  </strong>
                </ListGroup.Item>

                <ListGroup.Item className="d-flex justify-content-between fw-bold">
                  Total Amount:
                  <strong>
                    ₹{order.totalAmount?.toFixed(2) || "0.00"}
                  </strong>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mt-3 mt-md-0 d-flex flex-column">
          {canDeliver && (
            <Button
              className="my-2 action-btn deliver-btn"
              onClick={deliverHandler}
              disabled={loadingDeliver}
            >
              {loadingDeliver ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <FaCheckCircle className="me-2" />
                  Mark As Delivered
                </>
              )}
            </Button>
          )}

          {canDelete && (
            <Button
              className="my-2 action-btn delete-btn"
              onClick={deleteHandler}
              disabled={loadingDelete}
            >
              {loadingDelete ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <FaTrash className="me-2" />
                  Delete Order
                </>
              )}
            </Button>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default OrderScreen;
